import { useState, useCallback, useRef, useEffect } from "react";
import { createParseJob, getParseJobStatus, parseCv } from "@/lib/recruitmentApi";

export const ACTIVE_CV_IMPORT_STORAGE_KEY = "lenvia_active_parse_job";

export const useCvImportPipeline = ({
    onSuccess,
    onError,
    forceRefresh = false,
    storageKey = ACTIVE_CV_IMPORT_STORAGE_KEY,
} = {}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [importProgress, setImportProgress] = useState(null);
    const [importError, setImportError] = useState(null);
    const [importWarnings, setImportWarnings] = useState([]);
    const [lastImportResult, setLastImportResult] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [jobId, setJobId] = useState(null);

    const pollingTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    const clearActiveJob = useCallback(() => {
        try {
            localStorage.removeItem(storageKey);
        } catch {
            // ignore storage errors
        }
    }, [storageKey]);

    const stopPolling = useCallback(() => {
        if (pollingTimerRef.current) {
            clearTimeout(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
    }, []);

    const pollJob = useCallback(async (activeJobId, startedAt = Date.now(), fileRef = null) => {
        try {
            const statusResponse = await getParseJobStatus(activeJobId);
            if (!isMountedRef.current) return;

            if (statusResponse?.progress) {
                setImportProgress({
                    ...statusResponse.progress,
                    job_id: activeJobId,
                });
            }

            if (statusResponse?.status === "completed") {
                stopPolling();
                clearActiveJob();
                setIsLoading(false);
                const result = statusResponse.result || { success: true };
                setLastImportResult(result);
                if (result.warnings) {
                    setImportWarnings(result.warnings);
                }
                if (onSuccess) {
                    onSuccess(result, fileRef);
                }
                return result;
            }

            if (statusResponse?.status === "failed") {
                stopPolling();
                clearActiveJob();
                setIsLoading(false);
                const errorPayload = {
                    ...(statusResponse.error || {}),
                    job_id: activeJobId,
                    success: false,
                    error_code: statusResponse.error?.error_code || "JOB_FAILED",
                    error_message: statusResponse.error?.error_message || "L'analyse a échoué lors du traitement.",
                };
                setImportError(errorPayload);
                setLastImportResult(errorPayload);
                if (onError) {
                    onError(errorPayload);
                }
                return null;
            }

            // Still queued or running, schedule next poll
            pollingTimerRef.current = setTimeout(() => {
                pollJob(activeJobId, startedAt, fileRef);
            }, 1000);
        } catch {
            if (!isMountedRef.current) return;
            // Fallback or retry poll
            pollingTimerRef.current = setTimeout(() => {
                pollJob(activeJobId, startedAt, fileRef);
            }, 2000);
        }
    }, [clearActiveJob, stopPolling, onSuccess, onError]);

    const startImport = useCallback(async (file) => {
        if (!file) return null;
        setSelectedFile(file);
        setIsLoading(true);
        setImportError(null);
        setImportWarnings([]);
        setLastImportResult(null);
        setImportProgress(null);
        stopPolling();

        const startedAt = Date.now();

        try {
            // First attempt with createParseJob (background async pipeline with frise)
            let jobResponse;
            try {
                jobResponse = await createParseJob(file, forceRefresh);
            } catch (createErr) {
                // If createParseJob fails (e.g. mock test using parseCv, or synchronous fallback):
                if (typeof parseCv === "function") {
                    const fallbackResult = await parseCv(file, forceRefresh);
                    if (fallbackResult?.success === false || !fallbackResult?.draft) {
                        throw new Error(fallbackResult?.error_message || "Échec de l'import du CV", { cause: createErr });
                    }
                    setIsLoading(false);
                    setLastImportResult(fallbackResult);
                    if (onSuccess) onSuccess(fallbackResult, file);
                    return fallbackResult;
                }
                throw createErr;
            }

            const activeJobId = jobResponse?.job_id;
            setJobId(activeJobId);

            if (jobResponse?.progress) {
                setImportProgress({
                    ...jobResponse.progress,
                    job_id: activeJobId,
                });
            }

            try {
                localStorage.setItem(
                    storageKey,
                    JSON.stringify({ jobId: activeJobId, startedAt, filename: file.name })
                );
            } catch {
                // ignore
            }

            // Start polling
            pollJob(activeJobId, startedAt, file);
        } catch (err) {
            clearActiveJob();
            setIsLoading(false);
            const normalizedError = {
                job_id: jobId || "job_error",
                error_code: err?.response?.data?.error_code || "IMPORT_FAILED",
                error_message: err?.response?.data?.detail || err?.response?.data?.error_message || err?.message || "Erreur lors de l'import du CV",
            };
            setImportError(normalizedError);
            setLastImportResult(normalizedError);
            if (onError) onError(normalizedError);
            return null;
        }
    }, [clearActiveJob, stopPolling, forceRefresh, storageKey, pollJob, onSuccess, onError, jobId]);

    const retry = useCallback(() => {
        if (selectedFile) {
            startImport(selectedFile);
        }
    }, [selectedFile, startImport]);

    const reset = useCallback(() => {
        stopPolling();
        clearActiveJob();
        setIsLoading(false);
        setImportProgress(null);
        setImportError(null);
        setImportWarnings([]);
        setLastImportResult(null);
        setSelectedFile(null);
        setJobId(null);
    }, [stopPolling, clearActiveJob]);

    // Resumption on mount
    useEffect(() => {
        isMountedRef.current = true;
        const resumeActiveJob = async () => {
            try {
                const raw = localStorage.getItem(storageKey);
                if (!raw) return;
                const saved = JSON.parse(raw);
                if (!saved?.jobId) {
                    clearActiveJob();
                    return;
                }
                if (Date.now() - (saved.startedAt || 0) > 20 * 60 * 1000) {
                    clearActiveJob();
                    return;
                }
                const statusResponse = await getParseJobStatus(saved.jobId);
                if (!isMountedRef.current) return;
                if (statusResponse?.status === "queued" || statusResponse?.status === "running") {
                    setIsLoading(true);
                    setJobId(saved.jobId);
                    if (statusResponse.progress) {
                        setImportProgress({
                            ...statusResponse.progress,
                            job_id: saved.jobId,
                        });
                    }
                    pollJob(saved.jobId, saved.startedAt || Date.now());
                } else {
                    clearActiveJob();
                }
            } catch {
                clearActiveJob();
            }
        };
        resumeActiveJob();

        return () => {
            isMountedRef.current = false;
            stopPolling();
        };
    }, [clearActiveJob, stopPolling, pollJob, storageKey]);

    return {
        isLoading,
        importProgress,
        importError,
        importWarnings,
        lastImportResult,
        selectedFile,
        jobId,
        startImport,
        retry,
        reset,
    };
};
